import { Link } from "@tanstack/react-router";
import { ArrowLeft, DollarSign, Search } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { CostCode } from "@/hooks/use-cost-codes";
import { useCostCodes } from "@/hooks/use-cost-codes";

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
      Inactive
    </span>
  );
}

function OwnerPayAppBadge({ ownerPayApp }: { ownerPayApp: boolean }) {
  if (!ownerPayApp) return <span className="text-sm text-gray-400">--</span>;
  return (
    <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
      Yes
    </span>
  );
}

export function CostCodesPage() {
  const { data, isLoading, isError, error, refetch } = useCostCodes();
  const [search, setSearch] = useState("");

  const items = (data ?? []).filter((cc: CostCode) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      cc.code.toLowerCase().includes(q) ||
      cc.description.toLowerCase().includes(q) ||
      (cc.csiDivision ?? "").toLowerCase().includes(q) ||
      (cc.category ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/config">
            <button
              type="button"
              className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-[#334155] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#334155]">Cost Codes</h1>
            <p className="text-sm text-gray-500">
              {!isLoading && `${items.length} cost code${items.length !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Search by code, description, or division..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {/* Error */}
      {isError && <ErrorState message={error?.message} onRetry={() => refetch()} />}

      {/* Empty */}
      {!isLoading && !isError && items.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {search ? "No cost codes match your search." : "No cost codes found."}
          </p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && items.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Code
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Description
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  CSI Division
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  CSI Subdivision
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Category
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  ERP Code
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Item No
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-center">
                  Owner Pay App
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-center">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((cc: CostCode) => (
                <TableRow key={cc.id} className="hover:bg-gray-50">
                  <TableCell className="text-sm font-mono font-medium text-gray-900">
                    {cc.code}
                  </TableCell>
                  <TableCell className="text-sm text-gray-900">{cc.description}</TableCell>
                  <TableCell className="text-sm text-gray-600">{cc.csiDivision ?? "--"}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {cc.csiSubdivision ?? "--"}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{cc.category ?? "--"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{cc.erpCode ?? "--"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{cc.itemNo ?? "--"}</TableCell>
                  <TableCell className="text-center">
                    <OwnerPayAppBadge ownerPayApp={cc.ownerPayApp} />
                  </TableCell>
                  <TableCell className="text-center">
                    <ActiveBadge isActive={cc.isActive} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
